import type {
    Formatter,
    FormControlOptions,
    FormController,
    Modifier,
    Parser,
    SvelteFormsField,
    UpdateTrigger
} from './types';
import {asapScheduler, observeOn} from 'rxjs';
import type {Observable} from 'rxjs';
import {BehaviorSubject, Subject} from 'rxjs';
import {distinctUntilChanged, distinctUntilKeyChanged, filter, map, share, tap} from 'rxjs/operators';
import type {Readable, Unsubscriber} from 'svelte/store';
import {get} from 'svelte/store';
import type {Validator} from 'svelte-forms';
import {field} from 'svelte-forms';
import type {Field} from 'node_modules/svelte-forms/types';
import {LocalizationAwareError} from '../errors/localization-aware-error';
import {readableStoreToObservableAdapter} from 'core/utils';
import {isEqual} from 'lodash-es';
import type {ModelInputChangeEvent} from '../../events';
import {createModelInputChangeEvent} from '../../events';
import {shareWithRefCountAndReplayLast} from 'shared/utils/observables-utils';
import {deepFreeze, exists} from 'shared/utils/custom-utils';
import type {Logger} from 'core/logging/types';

export const defaultUpdateTriggers: Record<'INPUT' | 'BLUR', UpdateTrigger> = deepFreeze({
    INPUT: {eventName: 'input'},
    BLUR: {eventName: 'blur'}
})

export class FormControlBuilder<T> {

    private value: T = null;
    private validators: Validator[] = [];
    private parsers: Parser[] = [];
    private modifiers: Modifier[] = [];
    private formatters: Formatter[] = [];
    private formControlOptions: FormControlOptions = {updateTriggers: [defaultUpdateTriggers.BLUR, defaultUpdateTriggers.INPUT]};

    constructor(private id: string, private name: string) {}

    public static for<T>(id: string, name: string, value: T): FormControlBuilder<T> {
        return new FormControlBuilder<T>(id, name).withValue(value)
    }

    public withValue(value: T): FormControlBuilder<T> {
        this.value = value;
        return this;
    }

    public withValidators(validators: Validator[]): FormControlBuilder<T> {
        this.validators = validators
        return this;
    }

    public withParsers(parsers: Parser[]): FormControlBuilder<T> {
        this.parsers = parsers;
        return this;
    }

    public withModifiers(modifiers: Modifier[]): FormControlBuilder<T> {
        this.modifiers = modifiers;
        return this;
    }

    public withFormatters(formatters: Formatter[]): FormControlBuilder<T> {
        this.formatters = formatters;
        return this;
    }

    public withOptions(options: FormControlOptions | null): FormControlBuilder<T> {
        if (options !== null) {
            this.formControlOptions = options;
        }
        return this;
    }

    public build(logger: Logger): FormControl<T> {
        const formControl = new FormControl<T>(
            this.id,
            this.name,
            this.value,
            [...this.validators],
            {...this.formControlOptions},
            [...this.parsers],
            [...this.formatters],
            [...this.modifiers]
        );

        if (exists(logger)) {
            formControl.addLogger(logger);
        }

        return formControl;
    }
}

export class FormControl<T> implements FormController<T> {

    private modelValue: T;
    private lastCommittedStateValue: T;
    private readonly modelValue$: Observable<T>;
    private readonly modelInputChangeEvent$: Observable<ModelInputChangeEvent<T>>;
    private readonly fieldStateStore: SvelteFormsField<T>;
    private readonly fieldState$: Observable<Field<T>>;
    private readonly viewValue$: Observable<any>;
    private readonly errors$: Observable<string[]>;

    private touched = false;
    private readonly touched$: BehaviorSubject<boolean> = new BehaviorSubject(this.touched);
    private readonly pendingAsyncValidation$: BehaviorSubject<boolean> = new BehaviorSubject(false);

    private asyncValidators: Validator[] = [];
    private readonly asyncValidationStore: SvelteFormsField<T>;
    private asyncValidationUnsubscriber: Unsubscriber;

    private logger: Logger | undefined;

    constructor(public readonly id: string,
                public readonly name: string,
                value: T = null,
                private validators: Validator[] = [],
                public readonly formControlOptions: FormControlOptions = {updateTriggers: [defaultUpdateTriggers.BLUR, defaultUpdateTriggers.INPUT]},
                private parsers: Parser[] = [],
                private formatters: Formatter[] = [],
                private modifiers: Modifier[] = []) {

        this.modelValue = this.lastCommittedStateValue = value;

        // create `svelte-forms` field instant for SYNC validation
        this.fieldStateStore = field(name, this.lastCommittedStateValue, this.validators, {
            checkOnInit: true,
            stopAtFirstError: true
        });

        // create `svelte-forms` field instant for ASYNC validation
        this.asyncValidationStore = field(`${name}-async-validators`, this.lastCommittedStateValue, this.asyncValidators, {
            checkOnInit: false, // we explicitly call validate only after we add async validators
            stopAtFirstError: true
        });

        // wrap svelte store with rxjs subject to use pipe operators
        this.fieldState$ = readableStoreToObservableAdapter(this.fieldStateStore).pipe(
            tap({
                subscribe: () => this.asyncValidationUnsubscriber = this.subscribeToAsyncValidation(),
                unsubscribe: () => this.asyncValidationUnsubscriber()
            }),
            shareWithRefCountAndReplayLast()
        );

        const modelPipeline: Observable<{modelValue: T, isInputChange: boolean}> = this.fieldState$.pipe(
            map((fieldState) => ({
                modelValue: this.convertToModelValue(fieldState),
                isInputChange: this.isChangeFromInput(fieldState)
            })),
            distinctUntilChanged(({modelValue: model1}, {modelValue: model2}) => isEqual(model1, model2)),
            tap(({modelValue}) => this.modelValue = modelValue),
            shareWithRefCountAndReplayLast()
        );

        this.modelValue$ = modelPipeline.pipe(
            map(({modelValue}) => modelValue),
            shareWithRefCountAndReplayLast()
        );

        this.modelInputChangeEvent$ = modelPipeline.pipe(
            observeOn(asapScheduler), // to make input-change event emitting async, so the event is emitted only after the state, viewValue and modelValue are updated
            filter(({isInputChange}) => isInputChange),
            map(({modelValue}) => modelValue),
            map(createModelInputChangeEvent),
            share({
                connector: () => new Subject(),
                resetOnError: false,
                resetOnComplete: false,
                resetOnRefCountZero: true
            })
        );

        this.viewValue$ = this.fieldState$.pipe(
            distinctUntilKeyChanged('value', isEqual),
            map((state) => this.format(state.value)),
            shareWithRefCountAndReplayLast()
        );

        this.errors$ = this.fieldState$.pipe(
            map((fieldState) => fieldState.errors),
            shareWithRefCountAndReplayLast()
        );
    }

    public addLogger(logger: Logger): void {
        this.logger = logger;
    }

    public getFieldState$(): Observable<Field<T>> {
        return this.fieldState$;
    }

    public getFieldStateAsStore(): Readable<Field<T>> {
        return this.fieldStateStore;
    }

    public async validate(): Promise<void> {
        if (this.isAsyncValidationEnabled()) {
            this.pendingAsyncValidation$.next(true);
        }
        await Promise.allSettled([this.fieldStateStore.validate(), this.asyncValidationStore.validate()]);
    }

    public getErrors$(): Observable<string[]> {
        return this.errors$;
    }

    public setModelValue(newModelValue: T) {
        if (this.modelValue === newModelValue) { // if nothing changed, do not set value to prevent unnecessary formatting and validation
            return;
        }
        this.modelValue = newModelValue; // set model value here to distinguish between change from model and change form view
        this.commitStateValue(newModelValue);
    }

    public getModelValue$(): Observable<T> {
        return this.modelValue$;
    }

    public getModelInputChangeEvent$(): Observable<ModelInputChangeEvent<T>> {
        return this.modelInputChangeEvent$;
    }

    public setViewValue(viewValue: any) {
        try {
            const parsedValue = this.parse(viewValue);
            this.commitStateValue(parsedValue);
        } catch (error: unknown) {
            let errorMsg: string;
            if (error instanceof LocalizationAwareError) {
                errorMsg = error.localizationKey;
            } else {
                this.logger?.error(error);
                errorMsg = 'Unknown parse error';
            }
            this.lastCommittedStateValue = null;
            this.fieldStateStore.update((fieldState) => ({
                ...fieldState,
                value: this.lastCommittedStateValue,
                valid: false,
                invalid: true,
                dirty: true,
                errors: [errorMsg]
            }));
        }
    }

    public getViewValue$<U = any>(): Observable<U> {
        return this.viewValue$;
    }

    public addValidator(validator: Validator) {
        this.validators.push(validator);
    }

    public addAsyncValidator(validator: Validator) {
        this.asyncValidators.push(validator);
    }

    public isTouched(): boolean {
        return this.touched;
    }

    public setTouched(): void {
        this.touched = true;
        this.touched$.next(this.touched);
    }

    public isTouched$(): Observable<boolean> {
        return this.touched$.pipe(distinctUntilChanged());
    }

    public isAsyncValidationPending(): Observable<boolean> {
        return this.pendingAsyncValidation$.asObservable();
    }

    private commitStateValue(value: T): void {
        this.lastCommittedStateValue = value;
        const stateValue: T = isNullOrUndefined(value) ? this.convertValueToFieldState(value) : value; // can not set mull or undefined directly TODO: check if supported in future

        this.fieldStateStore.set(stateValue);
        if (this.isAsyncValidationEnabled()) {
            this.pendingAsyncValidation$.next(true);
            this.asyncValidationStore.set(stateValue);
        }
    }

    private isChangeFromInput(fieldState: Field<T>): boolean {
        return this.modify(fieldState.value) !== this.modelValue; // model value was set from outside in method `setModelValue` (field state was synced with model value)
    }

    private convertToModelValue(fieldState: Field<T>): T {
        if (!fieldState.valid) {
            return null;
        }

        try {
            return this.modify(fieldState.value);
        } catch (error) {
            this.logger?.error(error);
            let errorMsg: string;
            if (error instanceof LocalizationAwareError) {
                errorMsg = error.localizationKey;
            } else {
                errorMsg = 'Unknown value modify error';
            }
            // set to INVALID to prevent infinite loop (error -> update field -> modify pipeline -> error)
            this.fieldStateStore.update((state) => ({
                ...state,
                valid: false,
                invalid: true,
                dirty: true,
                errors: [errorMsg]
            }));
            return null;
        }
    }

    private modify(modelValue: T): T {
        return this.modifiers.reduce<T>((value, functor) => functor(value), modelValue);
    }

    private parse(viewValue: any): T {
        return this.parsers.reduce<T>((value, functor) => functor(value), viewValue);
    }

    private format(modelValue: T): any {
        return this.formatters.reduceRight((value, functor) => functor(value), modelValue);
    }

    // we need to recast return value because svelte-forms set works with Field<T> and T, but typings only supports T
    private convertValueToFieldState<RECAST = Field<T>>(value: T): Field<T> & RECAST {
        return {...get(this.fieldStateStore), value} as Field<T> & RECAST;
    }

    private isAsyncValidationEnabled(): boolean {
        return this.asyncValidators.length > 0;
    }

    private isSyncValidationError(errorCodes: string[]): boolean {
        return errorCodes[0].substring(0, 5) !== 'async'; // async errors are 'async' or 'async-{xxx}'
    }

    private subscribeToAsyncValidation(): Unsubscriber {
        return this.asyncValidationStore.subscribe((asyncValidationState) => {
            if (!this.isAsyncValidationEnabled()) {
                return;
            }
            if (asyncValidationState.value !== this.lastCommittedStateValue) {
                return; // ignore out of date / out of order async results
            }
            this.fieldStateStore.update((fieldState) => {
                if (fieldState.invalid && this.isSyncValidationError(fieldState.errors)) {
                    return fieldState; // enforce stopAtFirstError policy, sync errors have higher priority
                                       // emulate not even starting async validation by ignoring this state regardless of result
                }
                return {
                    ...fieldState,
                    valid: asyncValidationState.valid,
                    invalid: asyncValidationState.invalid,
                    errors: asyncValidationState.errors
                }
            });
            this.pendingAsyncValidation$.next(false);
        })
    }
}

function isNullOrUndefined(value: unknown): boolean {
    return value === null || value === undefined;
}